import axios from 'axios';
import { useCallback, useRef, useState } from 'react';

import { DCR_API_BASE_SERVER_URL } from '../config';

export interface PdfMargin {
  top?: string;
  right?: string;
  bottom?: string;
  left?: string;
}

// Mirrors the pdfOptions shape forwarded by api-server to the html-pdf-convertion-lambda.
export interface PdfOptions {
  displayHeaderFooter?: boolean;
  headerTemplate?: string;
  footerTemplate?: string;
  margin?: PdfMargin;
}

export const USE_PDF_SELECTOR = '[data-pdf-selector]';

const isPdfScopedRule = (selector: string): boolean => selector.includes(USE_PDF_SELECTOR);

const getRelevantStyles = (container: HTMLElement): string => {
  const rules: string[] = [];

  Array.from(document.styleSheets).forEach(sheet => {
    try {
      Array.from(sheet.cssRules).forEach(rule => {
        if (rule instanceof CSSStyleRule) {
          try {
            if (isPdfScopedRule(rule.selectorText) || container.querySelector(rule.selectorText)) {
              rules.push(rule.cssText);
            }
          } catch {
            // skip invalid selectors
          }
        } else if (rule instanceof CSSMediaRule) {
          const mediaRules: string[] = [];
          Array.from(rule.cssRules).forEach(innerRule => {
            if (innerRule instanceof CSSStyleRule) {
              try {
                if (isPdfScopedRule(innerRule.selectorText) || container.querySelector(innerRule.selectorText)) {
                  mediaRules.push(innerRule.cssText);
                }
              } catch {
                // skip invalid selectors
              }
            }
          });
          if (mediaRules.length) {
            rules.push(`@media ${rule.conditionText} { ${mediaRules.join('\n')} }`);
          }
        }
      });
    } catch {
      // skip cross-origin stylesheets
    }
  });

  return rules.join('\n');
};

const PDF_EXCLUDE_SELECTOR = '[data-pdf-exclude]' as const;

// Chromium's CSS @page margin takes priority over Puppeteer's page.pdf() margin option, so
// this must always mirror whatever margin is ultimately sent to the lambda in pdfOptions.
const buildPageMarginCss = (margin?: PdfMargin): string => {
  const { top = '24px', right = '16px', bottom = '24px', left = '16px' } = margin ?? {};
  return `@page { margin: ${top} ${right} ${bottom} ${left}; }`;
};

const buildPdfBaseStyles = (margin?: PdfMargin): string => `
  * { box-sizing: border-box; }
  body {
    font-family: "Roboto", sans-serif;
    padding: 24px;
    margin: 0;
    font-size: 14px;
    color: #212121;
    -webkit-print-color-adjust: exact;
  }
  ${buildPageMarginCss(margin)}
  table { border-collapse: collapse; width: 100%; }
  td, th { text-align: left; vertical-align: top; }
  img { max-width: 100%; height: auto; }
  h1, h2, h3, h4, h5, h6 { margin: 0 0 8px; }
  p { margin: 0 0 8px; }
  [data-pdf-page-break] { page-break-before: always; break-before: page; }
  [data-pdf-no-break] { page-break-inside: avoid; break-inside: avoid; }
`;

export const buildPdfHtml = (container: HTMLElement, margin?: PdfMargin): string => {
  const clone = container.cloneNode(true) as HTMLElement;

  clone.querySelectorAll(PDF_EXCLUDE_SELECTOR).forEach(el => el.remove());

  const relevantCss = getRelevantStyles(container);

  return `
    <html lang="en">
      <head>
        <link rel="preconnect" href="https://fonts.googleapis.com">
        <link href="https://fonts.googleapis.com/css2?family=Roboto:wght@300;400;500;700&display=swap" rel="stylesheet">
        <style>
          ${buildPdfBaseStyles(margin)}
          ${relevantCss}
        </style>
      </head>
      <body data-pdf-selector>
        ${clone.innerHTML}
      </body>
    </html>
  `.replace(/(\r\n|\n|\r)/gm, '');
};

const PDF_LOADING_HTML = `
  <html lang="en">
    <head>
      <link href="https://fonts.googleapis.com/css2?family=Roboto:wght@300;400;500&display=swap" rel="stylesheet">
      <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body {
          font-family: "Roboto", sans-serif;
          display: flex;
          align-items: center;
          justify-content: center;
          min-height: 100vh;
          background: #f5f5f5;
          color: #333;
        }
        .loader-container {
          text-align: center;
          padding: 48px;
          background: #fff;
          border-radius: 12px;
          box-shadow: 0 2px 12px rgba(0,0,0,0.08);
        }
        .spinner {
          width: 48px;
          height: 48px;
          border: 4px solid #e0e0e0;
          border-top-color: #1976d2;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
          margin: 0 auto 24px;
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        .loader-title {
          font-size: 20px;
          font-weight: 500;
          margin-bottom: 8px;
          color: #212121;
        }
        .loader-subtitle {
          font-size: 14px;
          color: #757575;
        }
      </style>
    </head>
    <body>
      <div class="loader-container">
        <div class="spinner"></div>
        <div class="loader-title">Generating PDF</div>
        <div class="loader-subtitle">Please wait, your document is being prepared…</div>
      </div>
    </body>
  </html>
`;

const downloadAsPdf = (container: HTMLElement, pdfOptions?: PdfOptions): void => {
  const pdfWindow = window.open();

  if (pdfWindow?.document) {
    pdfWindow.document.write(PDF_LOADING_HTML);
    pdfWindow.document.close();
  }

  const html = buildPdfHtml(container, pdfOptions?.margin);

  axios
    .post(
      `${DCR_API_BASE_SERVER_URL}/html-to-pdf`,
      { html, ...(pdfOptions ? { pdfOptions } : {}) },
      {
        responseType: 'blob',
        headers: { 'Content-Type': 'application/json', Accept: '*/*' },
      }
    )
    .then(response => {
      const file = new Blob([response.data], { type: 'application/pdf' });
      const fileURL = URL.createObjectURL(file);
      if (pdfWindow?.location?.href) pdfWindow.location.href = fileURL;
    })
    .catch(error => {
      pdfWindow?.close();
      console.error(error);
    });
};

export const usePdf = () => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [isGenerating, setIsGenerating] = useState(false);

  const handleDownloadPdf = useCallback((pdfOptions?: PdfOptions) => {
    if (!contentRef.current) return;

    setIsGenerating(true);
    downloadAsPdf(contentRef.current, pdfOptions);
    setIsGenerating(false);
  }, []);

  return { contentRef, isGenerating, handleDownloadPdf };
};
